/**
 * Filter bar for the Execution Ledger monitor.
 *
 * Mirrors [ActivityFilters.tsx](../activity/ActivityFilters.tsx) shape:
 * horizontal strip of date pickers, dropdowns, and free-text inputs.
 * Empty strings mean "no filter" so the parent's `toApiFilter()` drops
 * them from the wire payload.
 *
 * Handoff §3: date range + `kind` + `status` + `orgId` + `caseId`.
 * Also includes `userId` — the API accepts it (handoff §2.1) and
 * platform-admins routinely scope investigations to one user.
 *
 * Date state is ISO `YYYY-MM-DD` strings; the parent converts to
 * `from` / `to` ms-epoch values for the API. Default window is the
 * last 7 days (matches ActivityBrowser, plus the TTL is 90 days so
 * "default Last 7" leaves plenty of headroom).
 */

import type { ExecutionLedgerFilter } from "@/types/execution-ledger";

export interface ExecutionLedgerFilterState {
  /** ISO `YYYY-MM-DD` — converted to ms epoch by `toApiFilter()`. */
  fromDate: string;
  toDate: string;
  kind: "" | "mission" | "harness" | "upload";
  /** Terminal-only — completed / failed / canceled / awaiting_user_timeout.
   *  See plan §C: the ledger never carries running/queued/awaiting rows. */
  status: string;
  orgId: string;
  userId: string;
  caseId: string;
}

export const EMPTY_EXECUTION_FILTER: ExecutionLedgerFilterState = {
  fromDate: defaultFromDate(),
  toDate: defaultToDate(),
  kind: "",
  status: "",
  orgId: "",
  userId: "",
  caseId: "",
};

function defaultFromDate(): string {
  // Last 7 days by default — TTL window is 90 days so this is well within.
  const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}
function defaultToDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Set the from/to range to "last N days ending today" (UTC dates). */
export function applyLastNDays(
  state: ExecutionLedgerFilterState,
  days: number,
): ExecutionLedgerFilterState {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return {
    ...state,
    fromDate: from.toISOString().slice(0, 10),
    toDate: to.toISOString().slice(0, 10),
  };
}

/** Range presets surfaced as quick-pick chips. 90d matches the TTL ceiling
 *  (`EXECUTION_LEDGER_TTL_DAYS`, default 90) so it's effectively "all data". */
export const RANGE_PRESETS: Array<{ label: string; days: number }> = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

/** Convert ISO date string to UTC start-of-day ms-epoch. Returns
 *  undefined for an empty input so the API receives no `from`/`to`. */
function isoToEpochStart(iso: string): number | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00.000Z`);
  const t = d.getTime();
  return Number.isFinite(t) ? t : undefined;
}
function isoToEpochEnd(iso: string): number | undefined {
  if (!iso) return undefined;
  const d = new Date(`${iso}T23:59:59.999Z`);
  const t = d.getTime();
  return Number.isFinite(t) ? t : undefined;
}

/** Project the UI state down to the wire-format `ExecutionLedgerFilter`.
 *  Empty strings and undefined values are stripped so the API sees a
 *  minimal query. */
export function toApiFilter(
  state: ExecutionLedgerFilterState,
): ExecutionLedgerFilter {
  const out: ExecutionLedgerFilter = {};
  if (state.kind) out.kind = state.kind;
  if (state.status) out.status = state.status;
  if (state.orgId.trim()) out.orgId = state.orgId.trim();
  if (state.userId.trim()) out.userId = state.userId.trim();
  if (state.caseId.trim()) out.caseId = state.caseId.trim();
  const from = isoToEpochStart(state.fromDate);
  const to = isoToEpochEnd(state.toDate);
  if (from !== undefined) out.from = from;
  if (to !== undefined) out.to = to;
  return out;
}

const KINDS: Array<{ value: ExecutionLedgerFilterState["kind"]; label: string }> = [
  { value: "", label: "Any kind" },
  { value: "mission", label: "mission" },
  { value: "harness", label: "harness" },
  { value: "upload", label: "upload" },
];

const STATUSES: Array<{ value: string; label: string }> = [
  { value: "", label: "Any status" },
  { value: "completed", label: "completed" },
  { value: "failed", label: "failed" },
  { value: "canceled", label: "canceled" },
  { value: "awaiting_user_timeout", label: "awaiting_user_timeout" },
];

interface Props {
  value: ExecutionLedgerFilterState;
  onChange: (next: ExecutionLedgerFilterState) => void;
  /** Hide the Kind dropdown. The Checkpoints page pins `kind` to
   *  `harness` (it filters to a single harness), so exposing the
   *  selector there would only let the operator pick a value that
   *  renders an empty list. Default false = full ExecutionLedger bar. */
  hideKind?: boolean;
}

export function ExecutionLedgerFilters({ value, onChange, hideKind = false }: Props) {
  const set = <K extends keyof ExecutionLedgerFilterState>(
    key: K,
    v: ExecutionLedgerFilterState[K],
  ) => onChange({ ...value, [key]: v });

  // Detect which preset (if any) matches the current range — UTC start
  // boundaries, so the chip highlights consistently with `applyLastNDays`.
  const activePresetDays = matchPresetDays(value.fromDate, value.toDate);

  return (
    <div
      className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900"
      data-testid="execution-ledger-filters"
    >
      <div className="mb-2 flex flex-wrap items-center gap-1">
        <span className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Last
        </span>
        {RANGE_PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            onClick={() => onChange(applyLastNDays(value, p.days))}
            className={`rounded border px-2 py-0.5 text-xs ${
              activePresetDays === p.days
                ? "border-indigo-300 bg-indigo-100 text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950 dark:text-indigo-300"
                : "border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
            }`}
            data-testid={`execution-ledger-range-${p.days}d`}
          >
            {p.label}
          </button>
        ))}
        <span className="ml-2 text-xs text-slate-400">or pick a custom range:</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="From">
          <input
            type="date"
            value={value.fromDate}
            onChange={(e) => set("fromDate", e.target.value)}
            className={inputClass}
            data-testid="execution-ledger-filter-from"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={value.toDate}
            onChange={(e) => set("toDate", e.target.value)}
            className={inputClass}
            data-testid="execution-ledger-filter-to"
          />
        </Field>
        {!hideKind && (
          <Field label="Kind">
            <select
              value={value.kind}
              onChange={(e) =>
                set("kind", e.target.value as ExecutionLedgerFilterState["kind"])
              }
              className={inputClass}
              data-testid="execution-ledger-filter-kind"
            >
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Status">
          <select
            value={value.status}
            onChange={(e) => set("status", e.target.value)}
            className={inputClass}
            data-testid="execution-ledger-filter-status"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Org id">
          <input
            type="text"
            placeholder="e.g. org-xxx"
            value={value.orgId}
            onChange={(e) => set("orgId", e.target.value)}
            className={inputClass}
            data-testid="execution-ledger-filter-org"
          />
        </Field>
        <Field label="User id">
          <input
            type="text"
            value={value.userId}
            onChange={(e) => set("userId", e.target.value)}
            className={inputClass}
            data-testid="execution-ledger-filter-user"
          />
        </Field>
        <Field label="Case id">
          <input
            type="text"
            value={value.caseId}
            onChange={(e) => set("caseId", e.target.value)}
            className={inputClass}
            data-testid="execution-ledger-filter-case"
          />
        </Field>
        <button
          type="button"
          onClick={() => onChange(EMPTY_EXECUTION_FILTER)}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-sm hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
          data-testid="execution-ledger-filter-reset"
        >
          Reset
        </button>
      </div>
    </div>
  );
}

const inputClass =
  "rounded-md border border-slate-200 bg-white px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-950";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
      {label}
      {children}
    </label>
  );
}

/** Return the preset `days` value whose "today minus N days" boundary
 *  matches the current `fromDate`/`toDate` exactly (UTC day-resolution),
 *  or null when the range is custom. Used to highlight the active chip. */
function matchPresetDays(fromDate: string, toDate: string): number | null {
  const today = new Date().toISOString().slice(0, 10);
  if (toDate !== today) return null;
  for (const p of RANGE_PRESETS) {
    const d = new Date(Date.now() - p.days * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    if (d === fromDate) return p.days;
  }
  return null;
}
